import { useMemo, useState } from 'react';

import { padColumns } from 'utils/pad-columns';

import type { WithdrawalRequests } from 'api';
import { useGetDisbursementWithdrawalRequests } from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { apiParams } from './apiAdapter';
import { getColumns } from './columns';
import { WITHDRAWALS_REQUESTS_TABLE_TEST_ID } from './constants';
import { WithdrawalDetails } from './Details/WithdrawalDetails';
import type { LocalState } from './localState';

type Props = {
  localState: LocalState;
  updateCursorAfter: (value: string) => void;
};

export const WithdrawalsTable = ({ localState, updateCursorAfter }: Props) => {
  const organisationId = useOrganisationId<number>();

  const [selectedRowData, setSelectedRowData] = useState<WithdrawalRequests>();

  const { filters: globalFilters } = useGlobalFilters();

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const withdrawalRequestColumns = useMemo(
    () => padColumns(getColumns(findAccountByAccountNumber)),
    [findAccountByAccountNumber]
  );

  const { data: withdrawals, isFetching } =
    useGetDisbursementWithdrawalRequests(
      organisationId,
      apiParams({
        organisationId: organisationId.toString(),
        ...localState,
        ...globalFilters
      }),
      {
        query: {
          refetchOnMount: true
        }
      }
    );

  const { onScroll, data } = useVirtualList<WithdrawalRequests>({
    data: withdrawals?.data,
    initialRefValue: withdrawals?.meta?.cursor_next,
    uniqueKey: 'id',
    isFetching,
    updateCursorCallback: updateCursorAfter,
    resetConditions: [globalFilters, localState.filters, updateCursorAfter],
    cursorNext: withdrawals?.meta?.has_next_page
      ? withdrawals.meta?.cursor_next
      : undefined
  });

  const handleRowClick = (record: WithdrawalRequests) => {
    setSelectedRowData((selected) =>
      selected !== record ? record : undefined
    );
  };

  return (
    <DataTable
      total={withdrawals?.meta?.total}
      dataSource={data}
      columns={withdrawalRequestColumns}
      onScroll={onScroll}
      onRowClick={handleRowClick}
      isLoading={isFetching}
      rowKey="id"
      selectedRow={selectedRowData}
      tableDataTestId={WITHDRAWALS_REQUESTS_TABLE_TEST_ID}
      rightPanel={
        selectedRowData ? (
          <WithdrawalDetails
            onClose={() => setSelectedRowData(undefined)}
            selectedRowData={selectedRowData}
          />
        ) : undefined
      }
    />
  );
};
